import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { toast } from "sonner";
import {
  IconChevronLeft,
  IconPlus,
  IconTrash,
  IconDeviceFloppy,
  IconCopy,
  IconLayoutGrid,
  IconAlignLeft,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  useRelease,
  useEventRelease,
  useUpdateCommonCals,
  useEventReleasesWithCommonCals,
} from "@/api/hooks/use-releases";
import { asEventId, asReleaseId } from "@/types/identifiers";
import { formatValue, type NvpParameter } from "@/domain/calibration-params";
import { arrayTextStatus, toFixedLength } from "@/domain/nvp-array-text";
import type { components } from "@/api/generated/schema";

type EventReleaseWithEvent = components["schemas"]["EventReleaseWithEventResponse"];

interface ParameterDef {
  Name: string;
  DatasetName: string;
  ChpDataType: string;
  Value: unknown;
  Min: unknown;
  Max: unknown;
  Unit: string;
  Description: string;
  Accuracy: number;
  ElementCount: number;
  RowCount: number;
  ColCount: number;
}

interface CommonCalEntry extends ParameterDef {
  target_value: unknown;
}

function getAllParameters(paramDef: Record<string, unknown>): ParameterDef[] {
  // The NVP parameter_definition shape varies between releases — Datasets, or a
  // dataset's Items, can be absent or a non-array. Guard on Array.isArray so a
  // malformed dataset is skipped rather than crashing the editor with
  // "Items is not iterable" (the spread requires an iterable).
  const datasets = paramDef.Datasets;
  if (!Array.isArray(datasets)) return [];
  const params: ParameterDef[] = [];
  for (const dataset of datasets) {
    if (Array.isArray(dataset?.Items)) {
      params.push(...(dataset.Items as ParameterDef[]));
    }
  }
  return params;
}

/** Input width that fits a value at its allowed precision: sign + a few
 *  integer digits + decimal point + Accuracy decimal places (clamped). */
function valueInputWidth(accuracy: number): string {
  const dp = Math.max(0, Math.min(Math.round(accuracy || 0), 12));
  const chars = 5 + (dp > 0 ? dp + 1 : 0);
  return `calc(${chars}ch + 1.75rem)`; // + padding/spinner room
}

/** 1D-array params with more than this many elements default to free-text
 *  entry — a grid of 4+ boxes is already tedious to fill. */
const TEXT_MODE_THRESHOLD = 3;

function ValueEditor({
  param,
  name,
  value,
  onChange,
  onValidityChange,
}: {
  param: ParameterDef;
  name: string;
  value: unknown;
  onChange: (val: unknown) => void;
  onValidityChange: (name: string, valid: boolean) => void;
}) {
  const { ElementCount, RowCount, ColCount, Accuracy, ChpDataType } = param;
  const isScalar = ElementCount === 1;
  const isMatrix = RowCount > 1 && ColCount > 1;
  const is1DArray = !isScalar && !isMatrix;
  const width = valueInputWidth(Accuracy);

  // Ensure value is always an array for non-scalars
  const arr = Array.isArray(value) ? (value as number[]) : [value as number];

  // Free-form text view for 1D arrays. Anything bigger than 3 elements opens
  // in text mode by default — a grid of 4+ boxes is already tedious to fill.
  const serialized = useMemo(
    () => formatValue(value as NvpParameter["Value"], Accuracy, ChpDataType),
    [value, Accuracy, ChpDataType],
  );
  const [mode, setMode] = useState<"grid" | "text">(() =>
    is1DArray && ElementCount > TEXT_MODE_THRESHOLD ? "text" : "grid",
  );
  const [text, setText] = useState(serialized);
  const status = useMemo(
    () => arrayTextStatus(text, ElementCount),
    [text, ElementCount],
  );

  // Re-seed the textarea when `value` changes from outside (e.g. "Copy
  // from..."), while leaving in-progress typing untouched: when the text
  // already represents the same numbers, its re-serialization equals
  // `serialized`, so we skip. This is setState-during-render (not an effect)
  // and converges in one pass — re-seeding makes the next echo match.
  const textEcho = useMemo(
    () => formatValue(status.values, Accuracy, ChpDataType),
    [status, Accuracy, ChpDataType],
  );
  if (mode === "text" && textEcho !== serialized) {
    setText(serialized);
  }

  // Only the text view can be malformed; grid/scalar/matrix are valid by shape.
  const reportedValid = is1DArray && mode === "text" ? status.valid : true;
  useEffect(() => {
    onValidityChange(name, reportedValid);
  }, [name, reportedValid, onValidityChange]);
  useEffect(() => () => onValidityChange(name, true), [name, onValidityChange]);

  if (isScalar) {
    return (
      <Input
        type="number"
        value={String(value ?? "")}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="font-mono text-sm h-7 px-2 shrink-0"
        style={{ width }}
      />
    );
  }

  if (isMatrix) {
    // Grid of rows x cols; each column sized to the value's precision.
    return (
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${ColCount}, ${width})` }}
      >
        {Array.from({ length: RowCount }).map((_, row) =>
          Array.from({ length: ColCount }).map((_, col) => {
            const idx = row * ColCount + col;
            return (
              <Input
                key={idx}
                type="number"
                value={String(arr[idx] ?? "")}
                onChange={(e) => {
                  const newArr = [...arr];
                  newArr[idx] = parseFloat(e.target.value) || 0;
                  onChange(newArr);
                }}
                className="font-mono text-sm h-7 w-full px-1 text-center"
              />
            );
          })
        )}
      </div>
    );
  }

  // --- 1D array: grid or free-form text, with a per-row toggle ---
  const switchMode = () => {
    if (mode === "text") {
      // Normalize to the declared length so the grid is always well-formed.
      onChange(toFixedLength(status.values, ElementCount));
      setMode("grid");
    } else {
      setText(serialized);
      setMode("text");
    }
  };

  const toggle = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={switchMode}
      title={mode === "text" ? "Switch to grid entry" : "Switch to free-text entry"}
      className="h-7 w-7 p-0 text-muted-foreground shrink-0"
    >
      {mode === "text" ? (
        <IconLayoutGrid className="h-4 w-4" />
      ) : (
        <IconAlignLeft className="h-4 w-4" />
      )}
    </Button>
  );

  if (mode === "text") {
    const handleTextChange = (raw: string) => {
      setText(raw);
      onChange(arrayTextStatus(raw, ElementCount).values);
    };
    return (
      <div className="flex items-start gap-1.5">
        <div className="flex flex-col items-end gap-0.5">
          <Textarea
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            aria-invalid={!status.valid}
            spellCheck={false}
            placeholder={`${ElementCount} values, comma- or space-separated`}
            className="font-mono text-xs leading-snug min-h-0 w-[20rem] px-2 py-1 resize-y"
          />
          <span
            className={`font-mono text-[10px] ${
              status.valid ? "text-muted-foreground/60" : "text-red-600 dark:text-red-400"
            }`}
            title={
              status.invalidTokens.length
                ? `Not a number: ${status.invalidTokens.join(", ")}`
                : undefined
            }
          >
            {status.values.length}/{ElementCount}
          </span>
        </div>
        {toggle}
      </div>
    );
  }

  // Grid: exactly ElementCount inputs (independent of the stored array length).
  const cols = Math.min(ElementCount, 6);
  const handleGridChange = (index: number, newVal: number) => {
    const newArr = toFixedLength(arr, ElementCount);
    newArr[index] = newVal;
    onChange(newArr);
  };
  return (
    <div className="flex items-start gap-1.5">
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, ${width})` }}
      >
        {Array.from({ length: ElementCount }).map((_, i) => (
          <Input
            key={i}
            type="number"
            value={String(arr[i] ?? "")}
            onChange={(e) => handleGridChange(i, parseFloat(e.target.value) || 0)}
            className="font-mono text-sm h-7 w-full px-1 text-center"
          />
        ))}
      </div>
      {toggle}
    </div>
  );
}

function CopyFromDialog({
  open,
  onOpenChange,
  onCopy,
  currentEventId,
  currentReleaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCopy: (cals: CommonCalEntry[]) => void;
  currentEventId: string;
  currentReleaseId: string;
}) {
  const { data: sources, isLoading } = useEventReleasesWithCommonCals();

  // Filter out the current release
  const availableSources = sources?.filter(
    (s) => !(s.event_id === currentEventId && s.release_id === currentReleaseId)
  );

  const handleSelect = (source: EventReleaseWithEvent) => {
    const cals = source.common_cals as unknown as CommonCalEntry[];
    onCopy(cals);
    onOpenChange(false);
    toast.success(`Copied ${cals.length} parameters from ${source.event_name} / ${source.release_name}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy Common Cals From...</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-2 py-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : !availableSources || availableSources.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No other releases have common cals configured yet.
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto py-2">
            {availableSources.map((source) => (
              <button
                key={source.id}
                onClick={() => handleSelect(source)}
                className="w-full text-left p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{source.event_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {source.release_name} (v{source.build_version})
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {source.common_cals_count} params
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EditorContentProps {
  release: { name: string; parameter_definition?: Record<string, unknown> };
  initialCommonCals: CommonCalEntry[];
  onSave: (cals: CommonCalEntry[]) => void;
  isSaving: boolean;
  onBack: () => void;
  eventId: string;
  releaseId: string;
}

function EditorContent({
  release,
  initialCommonCals,
  onSave,
  isSaving,
  onBack,
  eventId,
  releaseId,
}: EditorContentProps) {
  const [commonCals, setCommonCals] = useState<CommonCalEntry[]>(initialCommonCals);
  const [addingParam, setAddingParam] = useState<string>("");
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  // A row reports itself invalid when its free-form text holds the wrong
  // number of values; Save stays disabled until every field is well-formed.
  const setFieldValidity = useCallback((fieldName: string, valid: boolean) => {
    setInvalidFields((prev) => {
      const has = prev.has(fieldName);
      if (valid === !has) return prev; // already in the right state
      const next = new Set(prev);
      if (valid) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  }, []);
  const hasInvalid = invalidFields.size > 0;

  const allParams = useMemo(
    () => (release.parameter_definition ? getAllParameters(release.parameter_definition) : []),
    [release]
  );

  const availableParams = useMemo(() => {
    const usedNames = new Set(commonCals.map((c) => c.Name));
    return allParams.filter((p) => !usedNames.has(p.Name));
  }, [allParams, commonCals]);

  const hasChanges = useMemo(() => {
    if (commonCals.length !== initialCommonCals.length) return true;
    return JSON.stringify(commonCals) !== JSON.stringify(initialCommonCals);
  }, [commonCals, initialCommonCals]);

  const handleAddParam = () => {
    if (!addingParam) return;
    const param = allParams.find((p) => p.Name === addingParam);
    if (!param) return;

    setCommonCals((prev) => [
      ...prev,
      { ...param, target_value: param.Value },
    ]);
    setAddingParam("");
  };

  const handleRemoveParam = (name: string) => {
    setCommonCals((prev) => prev.filter((c) => c.Name !== name));
  };

  const handleValueChange = (name: string, value: unknown) => {
    setCommonCals((prev) =>
      prev.map((c) => (c.Name === name ? { ...c, target_value: value } : c))
    );
  };

  const handleCopyFrom = (copiedCals: CommonCalEntry[]) => {
    // Merge copied cals with existing ones (replace if same Name)
    const byName = new Map(commonCals.map((c) => [c.Name, c]));
    for (const cal of copiedCals) {
      byName.set(cal.Name, cal);
    }
    setCommonCals(Array.from(byName.values()));
  };

  return (
    <>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-muted-foreground"
        >
          <IconChevronLeft className="h-4 w-4 mr-1" />
          Back to Releases
        </Button>
      </div>

      <PageHeader
        title={`Common Cals: ${release.name}`}
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCopyDialogOpen(true)}
            >
              <IconCopy className="h-4 w-4 mr-1" />
              Copy from...
            </Button>
            <Button
              onClick={() => onSave(commonCals)}
              disabled={isSaving || hasInvalid}
              variant="outline"
              title={
                hasInvalid
                  ? `Fix ${invalidFields.size} field(s) with the wrong number of values`
                  : undefined
              }
              className={
                hasChanges && !hasInvalid
                  ? "border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-500 hover:text-white"
                  : ""
              }
            >
              <IconDeviceFloppy className="h-4 w-4 mr-1" />
              {isSaving
                ? "Saving..."
                : hasInvalid
                  ? `Fix ${invalidFields.size} field${invalidFields.size > 1 ? "s" : ""}`
                  : "Save Changes"}
            </Button>
          </div>
        }
      />

      <p className="text-sm text-muted-foreground mt-2 mb-6">
        Select parameters and set target values that should be synchronized
        across all boats using this release.
      </p>

      <div className="flex gap-2 mb-6">
        <Combobox
          items={availableParams}
          value={availableParams.find((p) => p.Name === addingParam) ?? null}
          onValueChange={(val) => setAddingParam(val?.Name ?? "")}
          itemToStringLabel={(item) => item.Name}
          itemToStringValue={(item) => item.Name}
        >
          <ComboboxInput
            placeholder="Search parameters..."
            className="w-[400px]"
          />
          <ComboboxContent>
            <ComboboxEmpty>No parameters found.</ComboboxEmpty>
            <ComboboxList>
              {(param) => (
                <ComboboxItem key={param.Name} value={param}>
                  <div>
                    <div className="font-mono text-sm">{param.Name}</div>
                    {param.Description && (
                      <div className="text-xs text-muted-foreground truncate max-w-sm">
                        {param.Description}
                      </div>
                    )}
                  </div>
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <Button
          variant="outline"
          onClick={handleAddParam}
          disabled={!addingParam}
        >
          <IconPlus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {commonCals.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-muted-foreground">
          No parameters configured. Add parameters using the dropdown above.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {commonCals.map((cal) => (
            <div
              key={cal.Name}
              className="flex items-center gap-3 bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-2 py-1.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-[13px] font-medium">{cal.Name}</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                    {cal.DatasetName}
                  </Badge>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {cal.ChpDataType}
                    {cal.ElementCount > 1 && `[${cal.ElementCount}]`}
                  </span>
                  {cal.Unit && (
                    <span className="text-[10px] text-muted-foreground">· {cal.Unit}</span>
                  )}
                  {cal.Accuracy > 0 && (
                    <span className="text-[10px] text-muted-foreground/50">{cal.Accuracy}dp</span>
                  )}
                </div>
                {cal.Description && (
                  <div className="text-[11px] leading-tight text-muted-foreground/80 mt-0.5 truncate">
                    {cal.Description}
                  </div>
                )}
              </div>
              {/* Value editor floats right on every row — scalar input or
                  array/matrix grid alike, kept at its natural width. */}
              <div className="shrink-0 overflow-x-auto">
                <ValueEditor
                  param={cal}
                  name={cal.Name}
                  value={cal.target_value}
                  onChange={(v) => handleValueChange(cal.Name, v)}
                  onValidityChange={setFieldValidity}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveParam(cal.Name)}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
              >
                <IconTrash className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <CopyFromDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        onCopy={handleCopyFrom}
        currentEventId={eventId}
        currentReleaseId={releaseId}
      />
    </>
  );
}

export function CommonCalsEditorPage() {
  const { eventId: rawEventId, releaseId: rawReleaseId } = useParams<{
    eventId: string;
    releaseId: string;
  }>();
  const navigate = useNavigate();

  const eventId = asEventId(rawEventId ?? "");
  const releaseId = asReleaseId(rawReleaseId ?? "");

  const { data: release, isLoading: releaseLoading } = useRelease(releaseId);
  const { data: eventRelease, isLoading: erLoading } = useEventRelease(
    eventId,
    releaseId
  );
  const updateMut = useUpdateCommonCals(eventId, releaseId);

  const isLoading = releaseLoading || erLoading;

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!release || !eventRelease) {
    return (
      <div className="text-muted-foreground">
        Release not found
      </div>
    );
  }

  const initialCommonCals = (eventRelease.common_cals ?? []) as unknown as CommonCalEntry[];

  const handleSave = (cals: CommonCalEntry[]) => {
    updateMut.mutate(cals as unknown as Record<string, unknown>[], {
      onSuccess: () => {
        toast.success("Common cals saved");
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <EditorContent
      key={eventRelease.updated_at ?? eventRelease.created_at}
      release={release}
      initialCommonCals={initialCommonCals}
      onSave={handleSave}
      isSaving={updateMut.isPending}
      onBack={() => navigate(`/events/${rawEventId}/releases`)}
      eventId={rawEventId ?? ""}
      releaseId={rawReleaseId ?? ""}
    />
  );
}
